<template>
  <v-container fluid>
    <h1>Финансовый отчёт по услугам</h1>
    <div class="mb-5">
      <router-link to="/reports">
        Назад к списку отчётов
      </router-link>
    </div>

    <v-alert
      v-if="message.text"
      :type="message.type"
      dismissible
      class="mb-4"
      @input="clearMessage"
    >
      {{ message.text }}
    </v-alert>

    <v-card class="mb-6">
      <v-card-title>Сформировать отчёт</v-card-title>
      <v-card-text>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-select
              v-model="companyId"
              :items="companies"
              label="Филиал"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <v-menu
              v-model="menuFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateFrom"
                  label="Дата начала"
                  outlined
                  dense
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="dateFrom"
                :max="dateTo || undefined"
                first-day-of-week="1"
                @input="menuFrom = false"
              />
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <v-menu
              v-model="menuTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateTo"
                  label="Дата окончания"
                  outlined
                  dense
                  readonly
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="dateTo"
                :min="dateFrom || undefined"
                :max="maxDateTo"
                first-day-of-week="1"
                @input="menuTo = false"
              />
            </v-menu>
          </v-col>
        </v-row>

        <v-radio-group
          v-model="attendedOnly"
          row
          class="mt-4 mb-1"
          hide-details
        >
          <v-radio
            label="Все записи"
            :value="false"
          />
          <v-radio
            label="Только посещённые"
            :value="true"
          />
        </v-radio-group>

        <v-alert
          v-if="dateError"
          type="error"
          dense
          text
          class="mt-4 mb-0"
        >
          {{ dateError }}
        </v-alert>

        <v-btn
          color="primary"
          class="mt-5"
          :loading="creating"
          :disabled="!canCreate"
          @click="createReport"
        >
          Сформировать отчёт
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card class="mb-6">
      <v-card-title>Ранее сформированные отчёты</v-card-title>
      <v-card-text>
        <v-data-table
          :headers="reportHeaders"
          :items="reports"
          :loading="reportsLoading"
          :items-per-page="-1"
          hide-default-footer
          no-data-text="Отчёты ещё не формировались"
        >
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
          <template v-slot:item.period="{ item }">
            {{ formatDate(item.date_from) }} — {{ formatDate(item.date_to) }}
          </template>
          <template v-slot:item.attended_only="{ item }">
            {{ item.attended_only ? 'Посещённые' : 'Все записи' }}
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
              small
              :color="statusColor(item.status)"
              :text-color="item.status === 'PENDING' ? undefined : 'white'"
            >
              <v-progress-circular
                v-if="isInProgress(item.status)"
                indeterminate
                size="13"
                width="2"
                class="mr-2"
              />
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              v-if="item.status === 'READY'"
              text
              small
              color="primary"
              @click="openReport(item.id)"
            >
              Открыть
            </v-btn>
            <span
              v-else-if="item.status === 'FAILED'"
              class="error--text"
              :title="item.error_text"
            >
              {{ item.error_text || 'Ошибка формирования' }}
            </span>
            <span v-else>Ожидание</span>
          </template>
        </v-data-table>

        <v-pagination
          v-if="reportsTotalPages > 1"
          v-model="reportsPage"
          :length="reportsTotalPages"
          :total-visible="7"
          class="mt-4"
        />
      </v-card-text>
    </v-card>

    <v-card
      v-if="currentReport && isInProgress(currentReport.status)"
      class="mb-6"
    >
      <v-card-text class="d-flex align-center py-5">
        <v-progress-circular
          indeterminate
          color="primary"
          size="28"
          width="3"
          class="mr-4"
        />
        <div>
          <div class="text-subtitle-1 font-weight-medium">
            {{ statusLabel(currentReport.status) }}
          </div>
          <div class="text-body-2 grey--text">
            Отчёт № {{ currentReport.id }}. Данные обновятся автоматически.
          </div>
        </div>
      </v-card-text>
    </v-card>

    <template v-if="reportData">
      <div class="d-flex flex-wrap align-center justify-space-between mb-3">
        <div>
          <h2>Отчёт № {{ currentReport.id }}</h2>
          <div class="grey--text text-body-2">
            {{ companyName(currentReport.company_id) }},
            {{ formatDate(currentReport.date_from) }} —
            {{ formatDate(currentReport.date_to) }}
          </div>
        </div>
        <v-btn
          color="success"
          class="mt-2"
          @click="exportToExcel"
        >
          <v-icon
            left
            small
          >
            mdi-download
          </v-icon>
          Скачать Excel
        </v-btn>
      </div>

      <v-card class="mb-6">
        <v-card-title>Услуги</v-card-title>
        <v-card-text class="pa-0">
          <div class="report-table-wrap">
            <table class="report-table services-table">
              <thead>
                <tr>
                  <th>Категория / услуга</th>
                  <th>Количество оказанных услуг</th>
                  <th>Скидка, ₽</th>
                  <th>Абонементы, ₽</th>
                  <th>Оплачено со счетов клиентов, ₽</th>
                  <th>Оплачено деньгами, ₽</th>
                  <th>Прибыль, ₽</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="category in reportData.rows">
                  <tr
                    :key="`category-${category.category_id}`"
                    class="category-row"
                  >
                    <td>
                      <button
                        v-if="category.services.length"
                        type="button"
                        class="category-toggle"
                        @click="toggleCategory(category.category_id)"
                      >
                        <v-icon
                          small
                          class="mr-1"
                        >
                          {{ isCategoryExpanded(category.category_id)
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-right' }}
                        </v-icon>
                        {{ category.category }}
                        <span class="grey--text text--darken-1">
                          ({{ category.services.length }})
                        </span>
                      </button>
                      <span v-else>{{ category.category }}</span>
                    </td>
                    <td>{{ formatCount(category.services_count) }}</td>
                    <td>{{ formatMoney(category.discount) }}</td>
                    <td>{{ formatMoney(category.abonements) }}</td>
                    <td>{{ formatMoney(category.client_accounts) }}</td>
                    <td>{{ formatMoney(category.money) }}</td>
                    <td>{{ formatMoney(category.profit) }}</td>
                  </tr>
                  <template v-if="isCategoryExpanded(category.category_id)">
                    <tr
                      v-for="service in category.services"
                      :key="`service-${category.category_id}-${service.service_id}`"
                      class="service-row"
                    >
                      <td>{{ service.service_name }}</td>
                      <td>{{ formatCount(service.services_count) }}</td>
                      <td>{{ formatMoney(service.discount) }}</td>
                      <td>{{ formatMoney(service.abonements) }}</td>
                      <td>{{ formatMoney(service.client_accounts) }}</td>
                      <td>{{ formatMoney(service.money) }}</td>
                      <td>{{ formatMoney(service.profit) }}</td>
                    </tr>
                  </template>
                </template>
              </tbody>
              <tfoot>
                <tr>
                  <th>Всего</th>
                  <th>{{ formatCount(reportData.totals.services_count) }}</th>
                  <th>{{ formatMoney(reportData.totals.discount) }}</th>
                  <th>{{ formatMoney(reportData.totals.abonements) }}</th>
                  <th>{{ formatMoney(reportData.totals.client_accounts) }}</th>
                  <th>{{ formatMoney(reportData.totals.money) }}</th>
                  <th>{{ formatMoney(reportData.totals.profit) }}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        </v-card-text>
      </v-card>

      <v-card>
        <v-card-title>Доходы и расходы по кассам</v-card-title>
        <v-card-text class="pa-0">
          <div class="report-table-wrap">
            <table class="report-table cash-table">
              <thead>
                <tr>
                  <th>Статья</th>
                  <th
                    v-for="account in incomeExpense.accounts"
                    :key="account.account_id"
                  >
                    {{ account.account_name }}
                  </th>
                  <th>Всего</th>
                </tr>
              </thead>
              <tbody>
                <tr class="section-row income-section">
                  <th :colspan="incomeExpense.accounts.length + 2">
                    Доходы
                  </th>
                </tr>
                <tr
                  v-for="row in incomeExpense.income.rows"
                  :key="`income-${row.expense_id}`"
                >
                  <td>{{ row.expense_name }}</td>
                  <td
                    v-for="account in incomeExpense.accounts"
                    :key="account.account_id"
                  >
                    {{ formatMoney(accountAmount(row, account.account_id)) }}
                  </td>
                  <td>{{ formatMoney(row.total) }}</td>
                </tr>
                <tr class="subtotal-row">
                  <th>Итого доходы</th>
                  <th
                    v-for="account in incomeExpense.accounts"
                    :key="account.account_id"
                  >
                    {{ formatMoney(accountAmount(incomeExpense.income, account.account_id)) }}
                  </th>
                  <th>{{ formatMoney(incomeExpense.income.total) }}</th>
                </tr>

                <tr class="section-row expense-section">
                  <th :colspan="incomeExpense.accounts.length + 2">
                    Расходы
                  </th>
                </tr>
                <template v-for="row in incomeExpense.expenses.rows">
                  <tr :key="`expense-${row.expense_id}`">
                    <td>
                      <button
                        v-if="row.staff.length"
                        type="button"
                        class="category-toggle"
                        @click="toggleExpense(row.expense_id)"
                      >
                        <v-icon
                          small
                          class="mr-1"
                        >
                          {{ isExpenseExpanded(row.expense_id)
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-right' }}
                        </v-icon>
                        {{ row.expense_name }}
                        <span class="grey--text text--darken-1">
                          ({{ row.staff.length }})
                        </span>
                      </button>
                      <span v-else>{{ row.expense_name }}</span>
                    </td>
                    <td
                      v-for="account in incomeExpense.accounts"
                      :key="account.account_id"
                    >
                      {{ formatMoney(accountAmount(row, account.account_id)) }}
                    </td>
                    <td>{{ formatMoney(row.total) }}</td>
                  </tr>
                  <tr
                    v-for="staff in expandedExpenseStaff(row)"
                    :key="`staff-${row.expense_id}-${staff.staff_id}`"
                    class="staff-row"
                  >
                    <td>{{ staff.staff_name }}</td>
                    <td
                      v-for="account in incomeExpense.accounts"
                      :key="account.account_id"
                    >
                      {{ formatMoney(accountAmount(staff, account.account_id)) }}
                    </td>
                    <td>{{ formatMoney(staff.total) }}</td>
                  </tr>
                </template>
                <tr class="subtotal-row">
                  <th>Итого расходы</th>
                  <th
                    v-for="account in incomeExpense.accounts"
                    :key="account.account_id"
                  >
                    {{ formatMoney(accountAmount(incomeExpense.expenses, account.account_id)) }}
                  </th>
                  <th>{{ formatMoney(incomeExpense.expenses.total) }}</th>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th>Баланс</th>
                  <th
                    v-for="account in incomeExpense.accounts"
                    :key="account.account_id"
                  >
                    {{ formatMoney(accountAmount(incomeExpense.balance, account.account_id)) }}
                  </th>
                  <th>{{ formatMoney(incomeExpense.balance.total) }}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script>
import Api from '@/api/api'
import * as XLSX from 'xlsx'

export default {
  name: 'ServicesFinancialReport',

  data() {
    return {
      companies: [
        { text: 'Space Химки', value: 230691 },
        { text: 'Space ВДНХ', value: 992575 },
        { text: 'Залы Khimki BC', value: 232685 },
      ],
      companyId: 230691,
      dateFrom: null,
      dateTo: null,
      attendedOnly: false,
      menuFrom: false,
      menuTo: false,
      creating: false,
      reportsLoading: false,
      detailLoading: false,
      reports: [],
      reportsPage: 1,
      reportsCount: 0,
      pageSize: 10,
      currentReport: null,
      expandedCategories: {},
      expandedExpenses: {},
      pollingTimeout: null,
      pollingReportId: null,
      message: {
        type: 'info',
        text: '',
      },
      reportHeaders: [
        { text: 'Создан', value: 'created_at' },
        { text: 'Период', value: 'period', sortable: false },
        { text: 'Записи', value: 'attended_only', sortable: false },
        { text: 'Статус', value: 'status' },
        { text: 'Действие', value: 'action', sortable: false },
      ],
    }
  },

  computed: {
    maxDateTo() {
      if (!this.dateFrom) return undefined

      const [year, month, day] = this.dateFrom.split('-').map(Number)
      let maxDate = new Date(year + 1, month - 1, day)
      if (maxDate.getMonth() !== month - 1) {
        maxDate = new Date(year + 1, month, 0)
      }

      return this.toDateString(maxDate)
    },

    dateError() {
      if (!this.dateFrom || !this.dateTo) return ''
      if (this.dateFrom > this.dateTo) {
        return 'Дата окончания не может быть раньше даты начала'
      }
      if (this.dateTo > this.maxDateTo) {
        return 'Период отчёта не может превышать один год'
      }

      return ''
    },

    canCreate() {
      return Boolean(
        this.companyId
        && this.dateFrom
        && this.dateTo
        && !this.dateError
        && !this.creating,
      )
    },

    reportsTotalPages() {
      return Math.max(1, Math.ceil(this.reportsCount / this.pageSize))
    },

    reportData() {
      if (this.currentReport?.status !== 'READY') return null

      return this.currentReport.report_json || null
    },

    incomeExpense() {
      return this.reportData?.income_expense || {
        accounts: [],
        income: { rows: [], by_account: {}, total: 0 },
        expenses: { rows: [], by_account: {}, total: 0 },
        balance: { by_account: {}, total: 0 },
      }
    },
  },

  watch: {
    companyId() {
      this.stopPolling()
      this.currentReport = null
      if (this.reportsPage === 1) {
        this.loadReports()
      } else {
        this.reportsPage = 1
      }
    },

    reportsPage() {
      this.loadReports()
    },
  },

  created() {
    this.loadReports()
  },

  beforeDestroy() {
    this.stopPolling()
  },

  methods: {
    async loadReports() {
      this.reportsLoading = true

      try {
        const response = await Api.getFinancialReports({
          company_id: this.companyId,
          page: this.reportsPage,
          page_size: this.pageSize,
        })
        this.reports = response.results || []
        this.reportsCount = response.count || 0

        const activeReport = this.reports.find(report => this.isInProgress(report.status))
        if (activeReport && this.pollingReportId !== activeReport.id) {
          this.currentReport = activeReport
          this.startPolling(activeReport.id)
        }
      } catch (error) {
        this.showError(error, 'Не удалось загрузить список отчётов')
      } finally {
        this.reportsLoading = false
      }
    },

    async createReport() {
      if (!this.canCreate) return

      this.creating = true
      this.clearMessage()
      this.stopPolling()

      try {
        const response = await Api.createFinancialReport({
          company_id: this.companyId,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          attended_only: this.attendedOnly,
        })
        this.currentReport = {
          ...response,
          company_id: this.companyId,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          attended_only: this.attendedOnly,
        }
        this.startPolling(response.id)
        if (this.reportsPage === 1) {
          await this.loadReports()
        } else {
          this.reportsPage = 1
        }
      } catch (error) {
        const fallback = error.response?.status === 409
          ? 'Другой отчёт ещё формируется. Дождитесь его завершения.'
          : 'Не удалось запустить формирование отчёта'
        this.showError(error, fallback)
      } finally {
        this.creating = false
      }
    },

    async openReport(reportId) {
      this.detailLoading = true
      this.clearMessage()

      try {
        this.currentReport = await Api.getFinancialReport(reportId)
        if (this.isInProgress(this.currentReport.status)) {
          this.startPolling(reportId)
        } else {
          this.$nextTick(() => {
            const table = this.$el.querySelector('.services-table')
            if (table) table.scrollIntoView({ behavior: 'smooth', block: 'start' })
          })
        }
      } catch (error) {
        this.showError(error, 'Не удалось открыть отчёт')
      } finally {
        this.detailLoading = false
      }
    },

    startPolling(reportId) {
      if (this.pollingReportId === reportId) return

      this.stopPolling()
      this.pollingReportId = reportId

      const poll = async () => {
        try {
          const report = await Api.getFinancialReport(reportId)
          if (this.pollingReportId !== reportId) return

          this.currentReport = report

          if (report.status === 'READY') {
            this.pollingReportId = null
            this.pollingTimeout = null
            this.showMessage('success', 'Отчёт сформирован')
            await this.loadReports()

            return
          }

          if (report.status === 'FAILED') {
            this.pollingReportId = null
            this.pollingTimeout = null
            this.showMessage('error', report.error_text || 'Не удалось сформировать отчёт')
            await this.loadReports()

            return
          }
        } catch (error) {
          if (this.pollingReportId !== reportId) return
          this.showError(error, 'Не удалось обновить статус отчёта')
        }

        if (this.pollingReportId === reportId) {
          this.pollingTimeout = setTimeout(poll, 5000)
        }
      }

      poll()
    },

    stopPolling() {
      if (this.pollingTimeout) {
        clearTimeout(this.pollingTimeout)
        this.pollingTimeout = null
      }
      this.pollingReportId = null
    },

    isInProgress(status) {
      return status === 'PENDING' || status === 'PROCESSING'
    },

    statusLabel(status) {
      const labels = {
        PENDING: 'В очереди',
        PROCESSING: 'Формируется',
        READY: 'Готов',
        FAILED: 'Ошибка',
      }

      return labels[status] || status
    },

    statusColor(status) {
      const colors = {
        PENDING: 'grey lighten-2',
        PROCESSING: 'primary',
        READY: 'success',
        FAILED: 'error',
      }

      return colors[status] || 'grey'
    },

    companyName(companyId) {
      return this.companies.find(company => company.value === companyId)?.text || companyId
    },

    accountAmount(row, accountId) {
      return row?.by_account?.[String(accountId)] || 0
    },

    isCategoryExpanded(categoryId) {
      return Boolean(this.expandedCategories[categoryId])
    },

    toggleCategory(categoryId) {
      this.$set(
        this.expandedCategories,
        categoryId,
        !this.isCategoryExpanded(categoryId),
      )
    },

    isExpenseExpanded(expenseId) {
      return Boolean(this.expandedExpenses[expenseId])
    },

    toggleExpense(expenseId) {
      this.$set(
        this.expandedExpenses,
        expenseId,
        !this.isExpenseExpanded(expenseId),
      )
    },

    expandedExpenseStaff(row) {
      return this.isExpenseExpanded(row.expense_id) ? row.staff : []
    },

    exportToExcel() {
      if (!this.reportData) return

      const serviceRows = [[
        'Категория / услуга',
        'Количество оказанных услуг',
        'Скидка, ₽',
        'Абонементы, ₽',
        'Оплачено со счетов клиентов, ₽',
        'Оплачено деньгами, ₽',
        'Прибыль, ₽',
      ]]

      this.reportData.rows.forEach(category => {
        serviceRows.push(this.serviceExportRow(category.category, category))
        category.services.forEach(service => {
          serviceRows.push(this.serviceExportRow(`  ${service.service_name}`, service))
        })
      })
      serviceRows.push(this.serviceExportRow('Всего', this.reportData.totals))

      const accountIds = this.incomeExpense.accounts.map(account => account.account_id)
      const cashRows = [[
        'Статья',
        ...this.incomeExpense.accounts.map(account => account.account_name),
        'Всего',
      ]]

      cashRows.push(['Доходы'])
      this.incomeExpense.income.rows.forEach(row => {
        cashRows.push(this.cashExportRow(row.expense_name, row, accountIds))
      })
      cashRows.push(this.cashExportRow('Итого доходы', this.incomeExpense.income, accountIds))
      cashRows.push([])
      cashRows.push(['Расходы'])
      this.incomeExpense.expenses.rows.forEach(row => {
        cashRows.push(this.cashExportRow(row.expense_name, row, accountIds))
        row.staff.forEach(staff => {
          cashRows.push(this.cashExportRow(`  ${staff.staff_name}`, staff, accountIds))
        })
      })
      cashRows.push(this.cashExportRow('Итого расходы', this.incomeExpense.expenses, accountIds))
      cashRows.push([])
      cashRows.push(this.cashExportRow('Баланс', this.incomeExpense.balance, accountIds))

      const workbook = XLSX.utils.book_new()
      const servicesSheet = XLSX.utils.aoa_to_sheet(serviceRows)
      const cashSheet = XLSX.utils.aoa_to_sheet(cashRows)
      servicesSheet['!cols'] = [
        { wch: 55 },
        { wch: 18 },
        { wch: 15 },
        { wch: 18 },
        { wch: 28 },
        { wch: 24 },
        { wch: 18 },
      ]
      cashSheet['!cols'] = [
        { wch: 42 },
        ...this.incomeExpense.accounts.map(() => ({ wch: 20 })),
        { wch: 20 },
      ]

      XLSX.utils.book_append_sheet(workbook, servicesSheet, 'Услуги')
      XLSX.utils.book_append_sheet(workbook, cashSheet, 'Доходы и расходы')

      const filename = [
        'Финансовый_отчёт',
        this.currentReport.company_id,
        this.currentReport.date_from,
        this.currentReport.date_to,
      ].join('_')
      XLSX.writeFile(workbook, `${filename}.xlsx`)
    },

    serviceExportRow(label, row) {
      return [
        label,
        Number(row.services_count || 0),
        Number(row.discount || 0),
        Number(row.abonements || 0),
        Number(row.client_accounts || 0),
        Number(row.money || 0),
        Number(row.profit || 0),
      ]
    },

    cashExportRow(label, row, accountIds) {
      return [
        label,
        ...accountIds.map(accountId => Number(this.accountAmount(row, accountId))),
        Number(row.total || 0),
      ]
    },

    formatMoney(value) {
      return Number(value || 0).toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    },

    formatCount(value) {
      return Number(value || 0).toLocaleString('ru-RU', {
        maximumFractionDigits: 2,
      })
    },

    formatDate(value) {
      if (!value) return '—'

      const [year, month, day] = value.slice(0, 10).split('-').map(Number)

      return new Date(year, month - 1, day).toLocaleDateString('ru-RU')
    },

    formatDateTime(value) {
      if (!value) return '—'

      return new Date(value).toLocaleString('ru-RU', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    },

    toDateString(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    showError(error, fallback) {
      this.showMessage('error', error.response?.data?.detail || fallback)
    },

    showMessage(type, text) {
      this.message = { type, text }
    },

    clearMessage() {
      this.message = { type: 'info', text: '' }
    },
  },
}
</script>

<style scoped>
.report-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.report-table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  color: rgba(0, 0, 0, 0.87);
}

.report-table th,
.report-table td {
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  text-align: right;
  vertical-align: middle;
  white-space: nowrap;
}

.report-table th:first-child,
.report-table td:first-child {
  min-width: 260px;
  text-align: left;
  white-space: normal;
}

.report-table thead th {
  background: #eceff1;
  font-size: 12px;
  line-height: 1.25;
}

.report-table tbody td {
  font-size: 13px;
}

.services-table .category-row {
  background: #f5f7fa;
  font-weight: 600;
}

.category-toggle {
  display: inline-flex;
  align-items: center;
  color: inherit;
  font: inherit;
  text-align: left;
}

.services-table .service-row td:first-child,
.cash-table .staff-row td:first-child {
  padding-left: 30px;
}

.cash-table .staff-row {
  color: rgba(0, 0, 0, 0.62);
  font-size: 12px;
}

.report-table .section-row th {
  padding-top: 12px;
  padding-bottom: 12px;
  text-transform: uppercase;
}

.income-section th {
  background: #e8f5e9;
  color: #2e7d32;
}

.expense-section th {
  background: #ffebee;
  color: #c62828;
}

.report-table .subtotal-row th {
  background: #fff8e1;
}

.report-table tfoot th {
  background: #e3f2fd;
  font-size: 13px;
}

@media (max-width: 600px) {
  h1 {
    font-size: 26px;
  }
}
</style>
